import React from "react";
import Header from "@/components/Header";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Footer from "@/components/Footer";
import Container from "@/components/blocks/Container";
import Link from "next/link";
import { docsColors, docsComponents } from "@/lib/docs-theme";
import DocTitle from "@/components/docs/DocTitle";
import DocSection from "@/components/docs/DocSection";
import DocCodeBlock from "@/components/docs/DocCodeBlock";
import DocAlert from "@/components/docs/DocAlert";
import DocNavFooter from "@/components/docs/DocNavFooter";

export default function CliDocsPage() {
  return (
    <>
      <Head>
        {generateNextSeo({
          title: "CLI Tool - Uptime Monitor Documentation",
          description:
            "Manage monitors, contacts, incidents, and status pages from your terminal with the Uptime Monitor CLI.",
          canonical: "https://uptime-monitor.io/docs/cli",
          openGraph: {
            title: "CLI Tool - Uptime Monitor",
            description:
              "Command-line interface for Uptime Monitor - manage your monitoring infrastructure from the terminal",
            url: "https://uptime-monitor.io/docs/cli",
          },
        })}
      </Head>
      <div className={docsComponents.pageContainer}>
        <Header />
        <main className="flex-1">
          <Container>
            <div className="mx-auto max-w-4xl px-4 py-12">
              {/* Breadcrumb */}
              <nav className="mb-8">
                <ol className="flex items-center space-x-2 text-sm">
                  <li>
                    <Link href="/docs" className={docsColors.link.muted}>
                      Documentation
                    </Link>
                  </li>
                  <li className={docsColors.text.muted}>/</li>
                  <li className={docsColors.text.heading}>CLI Tool</li>
                </ol>
              </nav>

              <DocTitle
                title="CLI Tool"
                description="Manage monitors, contacts, incidents, and status pages from your terminal."
              />

              {/* Installation */}
              <DocSection title="Installation">
                <p className={`${docsColors.text.body} mb-4`}>
                  Install the pre-built binary for your platform (Linux and macOS, x86_64 and
                  ARM64). Add <code>--with-claude-skill</code> to also install the{" "}
                  <a
                    href="https://code.claude.com/docs/en/skills"
                    className={docsColors.link.primary}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Claude Code skill
                  </a>{" "}
                  for AI-assisted monitoring:
                </p>
                <DocCodeBlock copyText="curl -sSfL https://cli.uptime-monitor.io/install.sh | sh -s -- --with-claude-skill">
                  <pre className="text-sm pr-10">
                    <code>{`curl -sSfL https://cli.uptime-monitor.io/install.sh | sh [-s -- --with-claude-skill]`}</code>
                  </pre>
                </DocCodeBlock>
                <p className={`${docsColors.text.body} mt-4 text-sm`}>
                  The CLI installs to <code>/usr/local/bin/</code>. The skill installs to{" "}
                  <code>~/.claude/skills/uptime-monitor-cli/</code> and lets Claude Code manage
                  monitors, contacts, status pages, and incidents through natural language.
                </p>
                <p className={`${docsColors.text.body} mt-2 text-sm`}>
                  If the CLI is already installed, you can add the skill separately:
                </p>
                <DocCodeBlock copyText="uptime-monitor setup-skill">
                  <pre className="text-sm">
                    <code>{`uptime-monitor setup-skill`}</code>
                  </pre>
                </DocCodeBlock>
              </DocSection>

              {/* Authentication */}
              <DocSection title="Authentication">
                <p className={`${docsColors.text.body} mb-4`}>
                  Generate an API key from your{" "}
                  <a
                    href="https://uptime-monitor.io/dashboard/settings"
                    className={docsColors.link.primary}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Account Settings
                  </a>
                  , then authenticate:
                </p>
                <DocCodeBlock copyText="uptime-monitor auth">
                  <pre className="text-sm">
                    <code>{`# Interactive — opens browser to settings page, then prompts for key
uptime-monitor auth

# Non-interactive
uptime-monitor auth --api_key um_your_api_key_here`}</code>
                  </pre>
                </DocCodeBlock>
                <p className={`${docsColors.text.body} mt-4 mb-2 text-sm`}>
                  Credentials are stored in <code>~/.uptime-monitor/config.json</code> (file
                  permissions 0600). You can also use the environment variable:
                </p>
                <DocCodeBlock copyText="export UPTIME_MONITOR_API_KEY=um_your_api_key_here">
                  <pre className="text-sm">
                    <code>{`export UPTIME_MONITOR_API_KEY=um_your_api_key_here`}</code>
                  </pre>
                </DocCodeBlock>

                <DocAlert variant="info" className="mt-4">
                  <p className="text-sm">
                    <strong>Priority order:</strong> <code>--api_key</code> flag &gt;{" "}
                    <code>UPTIME_MONITOR_API_KEY</code> env var &gt; stored config file.
                  </p>
                </DocAlert>
              </DocSection>

              {/* Global Options */}
              <DocSection title="Global Options">
                <p className={`${docsColors.text.body} mb-4`}>
                  These options are available on all commands:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <th className={`text-left py-2 pr-4 ${docsColors.text.heading}`}>Flag</th>
                        <th className={`text-left py-2 ${docsColors.text.heading}`}>Description</th>
                      </tr>
                    </thead>
                    <tbody className={docsColors.text.body}>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4">
                          <code>--json</code>
                        </td>
                        <td className="py-2">Output raw JSON instead of formatted tables</td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4">
                          <code>--api-key &lt;KEY&gt;</code>
                        </td>
                        <td className="py-2">Override stored API key for this command</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">
                          <code>--api-url &lt;URL&gt;</code>
                        </td>
                        <td className="py-2">
                          Override API URL (default: <code>https://uptime-monitor.io</code>)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </DocSection>

              {/* Monitors */}
              <DocSection title="Monitors">
                <DocCodeBlock>
                  <pre className="text-sm">
                    <code>{`# List all monitors
uptime-monitor monitors list

# Filter by status or name
uptime-monitor monitors list --status up
uptime-monitor monitors list --search "Production"

# Account summary (up/down/paused counts)
uptime-monitor monitors status

# Get details for a specific monitor
uptime-monitor monitors get MONITOR_ID

# Create an HTTPS monitor
uptime-monitor monitors create \\
  --name "Production API" \\
  --url "https://api.example.com/health" \\
  --regions us-east-1,eu-west-1,ap-northeast-1 \\
  --interval 60 \\
  --timeout 10 \\
  --fail-threshold 2 \\
  --check-cert \\
  --contacts CONTACT_ID_1,CONTACT_ID_2

# Create a TCP monitor
uptime-monitor monitors create \\
  --name "PostgreSQL" \\
  --url "tcp://db.example.com:5432" \\
  --type tcp \\
  --regions us-east-1 \\
  --interval 120

# Create a Ping monitor
uptime-monitor monitors create \\
  --name "Gateway" \\
  --url "ping://10.0.0.1" \\
  --type ping \\
  --regions us-east-1

# Update a monitor
uptime-monitor monitors update MONITOR_ID \\
  --name "Updated Name" \\
  --interval 30 \\
  --regions us-east-1,us-west-1

# Pause / resume
uptime-monitor monitors pause MONITOR_ID
uptime-monitor monitors resume MONITOR_ID

# Delete (prompts for confirmation)
uptime-monitor monitors delete MONITOR_ID
uptime-monitor monitors delete MONITOR_ID --yes  # skip prompt`}</code>
                  </pre>
                </DocCodeBlock>

                <h3 className={`font-semibold ${docsColors.text.heading} mt-6 mb-3`}>
                  HTTPS-Specific Options
                </h3>
                <p className={`${docsColors.text.body} mb-3 text-sm`}>
                  These flags apply to <code>create</code> and <code>update</code> for HTTPS
                  monitors:
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <th className={`text-left py-2 pr-4 ${docsColors.text.heading}`}>Flag</th>
                        <th className={`text-left py-2 ${docsColors.text.heading}`}>Description</th>
                      </tr>
                    </thead>
                    <tbody className={docsColors.text.body}>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4">
                          <code>--method &lt;METHOD&gt;</code>
                        </td>
                        <td className="py-2">
                          HTTP method: HEAD, GET, POST, PUT, PATCH, DELETE (default: HEAD)
                        </td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4">
                          <code>--check-cert</code> / <code>--no-check-cert</code>
                        </td>
                        <td className="py-2">Enable/disable SSL certificate expiration checks</td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4">
                          <code>--no-follow-redirect</code>
                        </td>
                        <td className="py-2">Disable following HTTP redirects</td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4">
                          <code>--request-header &quot;Name: Value&quot;</code>
                        </td>
                        <td className="py-2">Add request header (repeatable)</td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4">
                          <code>--request-body &lt;BODY&gt;</code>
                        </td>
                        <td className="py-2">Request body for POST/PUT/PATCH</td>
                      </tr>
                      <tr className={`border-b ${docsColors.border.default}`}>
                        <td className="py-2 pr-4">
                          <code>--response-body &lt;TEXT&gt;</code>
                        </td>
                        <td className="py-2">Expected substring in response body</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-4">
                          <code>--expected-statuses &lt;CODES&gt;</code>
                        </td>
                        <td className="py-2">
                          Expected status codes (e.g., <code>&quot;200,201-204&quot;</code>)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </DocSection>

              {/* Contacts */}
              <DocSection title="Contacts">
                <DocCodeBlock>
                  <pre className="text-sm">
                    <code>{`# List all contacts
uptime-monitor contacts list

# Filter by channel type
uptime-monitor contacts list --channel slack

# Get contact details
uptime-monitor contacts get CONTACT_ID

# Create contacts (each channel type has specific flags)
uptime-monitor contacts create email --name "Alerts" --email alerts@example.com
uptime-monitor contacts create webhook --name "Hook" --url https://example.com/webhook
uptime-monitor contacts create slack --name "#alerts" --webhook-url https://hooks.slack.com/services/...
uptime-monitor contacts create discord --name "#monitoring" --webhook-url https://discord.com/api/webhooks/...
uptime-monitor contacts create ms-teams --name "Ops Channel" --webhook-url https://...

# PagerDuty with options
uptime-monitor contacts create pagerduty \\
  --name "On-Call" \\
  --integration-key R01234567890123456789012345678901

# Opsgenie with EU instance and priority
uptime-monitor contacts create opsgenie \\
  --name "Opsgenie" \\
  --opsgenie-api-key xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx \\
  --priority P2 \\
  --eu

# Zendesk
uptime-monitor contacts create zendesk \\
  --name "Support Tickets" \\
  --subdomain company \\
  --email support@example.com \\
  --api-token xxxxx

# incident.io
uptime-monitor contacts create incidentio \\
  --name "Incident.io" \\
  --webhook-url https://api.incident.io/webhooks/... \\
  --bearer-token inc_xxxxx

# Update a contact
uptime-monitor contacts update CONTACT_ID --name "New Name"

# Test a contact (sends test notification)
uptime-monitor contacts test CONTACT_ID

# Reactivate a deactivated contact
uptime-monitor contacts reactivate CONTACT_ID

# Delete
uptime-monitor contacts delete CONTACT_ID`}</code>
                  </pre>
                </DocCodeBlock>

                <DocAlert variant="info" className="mt-4">
                  <p className="text-sm">
                    All create commands accept <code>--down-alerts-only</code> to only receive
                    notifications when monitors go down (skip recovery alerts).
                  </p>
                </DocAlert>
              </DocSection>

              {/* Incidents */}
              <DocSection title="Incidents">
                <DocCodeBlock>
                  <pre className="text-sm">
                    <code>{`# List recent incidents
uptime-monitor incidents list

# Filter by monitor or status
uptime-monitor incidents list --monitor-id MONITOR_ID
uptime-monitor incidents list --unresolved
uptime-monitor incidents list --resolved --limit 10

# Get incident details (includes per-region check results)
uptime-monitor incidents get INCIDENT_ID

# Add or update a comment
uptime-monitor incidents comment INCIDENT_ID \\
  --comment "Root cause: memory leak in worker process"`}</code>
                  </pre>
                </DocCodeBlock>
              </DocSection>

              {/* Status Pages */}
              <DocSection title="Status Pages">
                <DocCodeBlock>
                  <pre className="text-sm">
                    <code>{`# List status pages
uptime-monitor status-pages list

# Get details
uptime-monitor status-pages get STATUS_PAGE_ID

# Create a status page
uptime-monitor status-pages create \\
  --name "Service Status" \\
  --monitors MONITOR_ID_1,MONITOR_ID_2 \\
  --period 30 \\
  --show-incident-reasons

# With custom domain and basic auth
uptime-monitor status-pages create \\
  --name "Internal Status" \\
  --monitors MONITOR_ID_1 \\
  --custom-domain status.example.com \\
  --basic-auth "viewer:secretpass"

# Update
uptime-monitor status-pages update STATUS_PAGE_ID \\
  --monitors MONITOR_ID_1,MONITOR_ID_2,MONITOR_ID_3 \\
  --period 90

# Remove basic auth
uptime-monitor status-pages update STATUS_PAGE_ID --remove-basic-auth

# Delete
uptime-monitor status-pages delete STATUS_PAGE_ID`}</code>
                  </pre>
                </DocCodeBlock>
              </DocSection>

              {/* Scripting & CI/CD */}
              <DocSection title="Scripting & CI/CD">
                <p className={`${docsColors.text.body} mb-4`}>
                  Use <code>--json</code> on any command for machine-readable output, and{" "}
                  <code>--yes</code> on delete commands to skip confirmation prompts:
                </p>
                <DocCodeBlock>
                  <pre className="text-sm">
                    <code>{`# Get monitor IDs as JSON for scripting
uptime-monitor monitors list --json | jq '.monitors[].id'

# CI/CD: create monitor with env var auth
export UPTIME_MONITOR_API_KEY=um_your_key
uptime-monitor monitors create \\
  --name "Staging API" \\
  --url "https://staging.example.com/health" \\
  --regions us-east-1 \\
  --interval 300 \\
  --json

# Cleanup in teardown scripts
uptime-monitor monitors delete MONITOR_ID --yes`}</code>
                  </pre>
                </DocCodeBlock>
              </DocSection>

              <DocNavFooter
                prev={{
                  href: "/docs/terraform",
                  text: "Terraform Provider",
                }}
                next={{
                  href: "/docs/claude-code-skill",
                  text: "Claude Code Skill",
                }}
              />
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    </>
  );
}
